import React, { useState, useEffect } from 'react';
import { 
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
  Button,
  InputField,
  SingleSelect,
  SingleSelectOption,
  CircularLoader,
  Chip,
} from '@dhis2/ui';
import { POSITION_ATTRIBUTES, ROLE_LEVEL_MAP  } from '../api/constants';
import { useDataEngine } from '@dhis2/app-runtime';
import { PositionHistoryService } from '../services/PositionHistoryService';
import { STAFF_TEI_TYPE, STAFF_ATTRIBUTES } from '../api/constants';
import { extractAttributes } from '../api/positionApi';
import { manageReportingAccess } from '../services/accessManagement';

const PositionDialog = ({ open, position, staffList, onSave, onClose, positionsList = [] }) => {
const [formData, setFormData] = useState({
    positionId: '',
    positionTitle: '',    
    jobGrade: '',
    status: 'Vacant',
    currentIncumbent: '',
    reportingTo: '',
    staffOrgUnit: ''
  });

  const [staffOptions, setStaffOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [positionHistory, setPositionHistory] = useState([]); 
  const [showHistory, setShowHistory] = useState(false);
  const [reportingOptions, setReportingOptions] = useState([]);
 
  const [isLoading, setIsLoading] = useState(true);

  const [recentJobGrades, setRecentJobGrades] = useState([]);
        
  const engine = useDataEngine();
  const [historyService] = useState(() => new PositionHistoryService(engine));

  // Helper: return lowest common ancestor id from user's orgUnit paths
const findLocalRootOrgUnit = (userOus = []) => {
  if (!userOus || userOus.length === 0) return null;
  const splitPaths = userOus
    .map(ou => (ou.path || '').split('/').filter(Boolean))
    .filter(p => p.length > 0);

  if (splitPaths.length === 0) return null;

  let rootId = null;
  for (let idx = 0; ; idx++) {
    const idsAtLevel = splitPaths.map(p => p[idx]).filter(Boolean);
    if (idsAtLevel.length !== splitPaths.length) break;
    const first = idsAtLevel[0];
    if (!idsAtLevel.every(id => id === first)) break;
    rootId = first;
  }
  return rootId;
};

// Fetch full org units (used to build subtree)
const fetchOrganisationUnits = async () => {
  const q = {
    orgUnits: {
      resource: 'organisationUnits',
      params: { paging: false, fields: 'id,name,level,path,parent[id,name]' }
    }
  };
  const r = await engine.query(q);
  return (r.orgUnits && r.orgUnits.organisationUnits) || [];
};

// Load current user and compute local root OU id (from current user's assigned OUs)
const [currentUser, setCurrentUser] = useState(null);
useEffect(() => {
  const loadMe = async () => {
    try {
      const r = await engine.query({
        me: {
          resource: 'me',
          params: { fields: 'id,name,organisationUnits[id,name,level,path]' }
        }
      });
      setCurrentUser(r.me || null);
    } catch (err) {
      console.error('Failed to load me', err);
    }
  };
  loadMe();
}, [engine]);

useEffect(() => {
  const loadRootOrgUnit = async () => {
    if (!currentUser || !open) {
      console.log('Skipping root org unit load:', { currentUser, open });
      return;
    }

    try {
      console.log('🔍 Loading root org unit for user:', currentUser.id);
      
      // Fetch all organisation units
      const { orgUnits } = await engine.query({
        orgUnits: {
          resource: "organisationUnits",
          params: {
            paging: false,
            fields: "id,name,level,path"
          }
        }
      });

      const units = orgUnits.organisationUnits || [];
      
      if (units.length === 0) {
        console.error("❌ No organisation units found in DHIS2");
        return;
      }

      // Find local root org unit from user's org units
      const localRootId = findLocalRootOrgUnit(currentUser.organisationUnits);
      
      if (!localRootId) {
        console.warn("⚠️ Could not determine root org unit from user's org units");
        // Fallback to user's first org unit
        const fallbackId = currentUser.organisationUnits?.[0]?.id;
        if (fallbackId) {
          const fallbackOu = units.find(ou => ou.id === fallbackId);
          setRootOrgUnit(fallbackOu);
          console.log('✅ Using fallback org unit:', fallbackOu?.name);
        }
        return;
      }

      // Find the root org unit object
      const localRootOu = units.find(ou => ou.id === localRootId);
      
      if (localRootOu) {
        setRootOrgUnit(localRootOu);
        console.log('✅ Root org unit loaded:', localRootOu.name, localRootOu.id);
      } else {
        console.error('❌ Root org unit not found in available units:', localRootId);
      }

    } catch (error) {
      console.error("❌ Failed to load root organisation unit:", error);
    }
  };

  // Only load when dialog opens and we have currentUser
  if (open && currentUser) {
    loadRootOrgUnit();
  }
}, [currentUser, open, engine]);


useEffect(() => {
    if (positionsList && positionsList.length > 0) {
      const grades = positionsList
        .map(pos => {
          const attrs = extractAttributes(pos.attributes);
          return attrs[POSITION_ATTRIBUTES.JOB_GRADE];
        })
        .filter(grade => grade && grade.trim() !== '')
        .filter((grade, index, self) => self.indexOf(grade) === index) // Unique
        .slice(0, 10); // Show only 10 most recent unique grades
      
      setRecentJobGrades(grades);
    }
  }, [positionsList]);

// FIXED: Consolidated form data initialization
useEffect(() => {
  if (open) {
    setIsLoading(true);
    
    const initializeFormData = async () => {
      let initialData = {
        positionId: '',
        positionTitle: '',
        currentIncumbent: '',
        reportingTo: '',
        jobGrade: '',
        status: 'Vacant',
      };

      let rawReportingToValue = '';

      if (position) {
        const extractAttributes = (attrs) => {
          const result = {};
          attrs.forEach(attr => {
            result[attr.attribute] = attr.value;
          });
          return result;
        };
                
        const positionAttrs = extractAttributes(position.attributes || []);
        
          
        
               
        const isDhis2Uid = (value) => {
          if (typeof value !== 'string') return false;
          const trimmed = value.trim();
              return /^[a-zA-Z0-9]{11}$/.test(trimmed);
        };

        // Extract raw job grade
        const rawJobGrade = positionAttrs[POSITION_ATTRIBUTES.JOB_GRADE] || '';

        // Only show it if it's NOT a UID (i.e., assume it's valid free text)
        const displayJobGrade = isDhis2Uid(rawJobGrade) ? '' : rawJobGrade;

        initialData = {
          positionId: positionAttrs[POSITION_ATTRIBUTES.POSITION_ID] || '',
          positionTitle: positionAttrs[POSITION_ATTRIBUTES.POSITION_TITLE] || '',
          currentIncumbent: positionAttrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT] || '',
          reportingTo: positionAttrs[POSITION_ATTRIBUTES.REPORTING_TO] || '',
          jobGrade: displayJobGrade, 
          status: positionAttrs[POSITION_ATTRIBUTES.POSITION_STATUS] || 'Active',
        };
      }

      setFormData(initialData);
      setIsLoading(false);
    };

    initializeFormData();
  }
}, [open, position]); 

  useEffect(() => {
    return () => {
      setIsLoading(false);
    };
  }, []);

// Compute rootOrgUnit (local root) and subtree IDs set
const [rootOrgUnit, setRootOrgUnit] = useState(null);

  // Debug effect for open prop
  useEffect(() => {
    console.log('🎯 PositionDialog - open prop:', open);
  }, [open]);

  // Load position history
  useEffect(() => {
    const loadPositionHistory = async () => {
      if (open && position && position.trackedEntityInstance) {
        try {
          console.log('📋 Loading history for position:', position.trackedEntityInstance);
          const history = await historyService.getPositionHistory(position.trackedEntityInstance);
          setPositionHistory(history);
          console.log('✅ Loaded position history:', history);
        } catch (error) {
          console.error('❌ Failed to load position history:', error);
          setPositionHistory([]);
        }
      } else {
        setPositionHistory([]);
        setShowHistory(false);
      }
    };

    loadPositionHistory();
  }, [open, position, historyService]);

 
  // NEW: Prepare reporting options from positionsList
  useEffect(() => {
    if (positionsList && positionsList.length > 0) {
      console.log('📊 Preparing reporting options from positions list:', positionsList.length);
      
      const options = positionsList
        .map(position => {
          const attributes = position.attributes || [];
          const positionAttrs = {};
          attributes.forEach(attr => {
            positionAttrs[attr.attribute] = attr.value;
          });

          const positionId = positionAttrs[POSITION_ATTRIBUTES.POSITION_ID] || 'No ID';
          const positionTitle = positionAttrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Untitled Position';
                 const currentIncumbentId = positionAttrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
          
          let staffDetails = null;
          if (currentIncumbentId && staffList) {
            staffDetails = staffList.find(staff => staff.id === currentIncumbentId);
          }

          let displayLabel = `${positionTitle} (${positionId})`;
          if (staffDetails) {
            const firstName = staffDetails.firstName || '';
            const surname = staffDetails.surname || '';
            displayLabel = `${positionTitle} - ${firstName} ${surname}`.trim() + ` (${positionId})`;
          } else {
            displayLabel = `${positionTitle} - VACANT (${positionId})`;
          }

          return {
            id: position.trackedEntityInstance,
            value: position.trackedEntityInstance,
            label: displayLabel,
            positionId: positionId,
            positionTitle: positionTitle,          
            staffDetails: staffDetails,
            isVacant: !staffDetails
          };
        })
        .filter(option => {
          if (position && position.trackedEntityInstance) {
            return option.id !== position.trackedEntityInstance;
          }
          return true;
        })
        .sort((a, b) => a.positionTitle.localeCompare(b.positionTitle));

      console.log('✅ Reporting options prepared:', options.length);
      setReportingOptions(options);
    } else {
      setReportingOptions([]);
      console.log('❌ No positions list available for reporting field');
    }
  }, [positionsList, staffList, position]);

  // Function to generate unique Position ID
    const generatePositionId = (positionTitle) => {
      if (!positionTitle) return '';
      
      // Clean title (first 3 capital letters)
      const cleanTitle = positionTitle
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 3);
      
      const numericSuffix = Math.floor(1000 + Math.random() * 9000);
      
      return `POS-${cleanTitle}-${numericSuffix}`; 
   };


 // Filter staff options based on search term
  const filteredStaffOptions = searchTerm
    ? staffOptions.filter(option => 
        option.searchText.includes(searchTerm.toLowerCase())
      )
    : staffOptions;

  // Handle staff selection - AUTO-FILL DEPARTMENT
const handleStaffSelection = (selectedStaffId) => {
  if (selectedStaffId === 'VACANT') {
    setFormData(prev => ({
      ...prev,
          currentIncumbent: '',
          status: 'Vacant',
          staffOrgUnit: '' // reset staff org unit
        }));
        return;
      }

      const selectedStaff = staffOptions.find(option => option.id === selectedStaffId);
      if (selectedStaff?.rawStaff) {
        let staffOrgUnit = '';
        if (selectedStaff.rawStaff.organisationUnits?.length > 0) {
          const sortedOUs = [...selectedStaff.rawStaff.organisationUnits].sort((a, b) => b.level - a.level);
          staffOrgUnit = sortedOUs[0].id;
            }

        setFormData(prev => ({
          ...prev,
          currentIncumbent: selectedStaffId,
          status: 'Active',
          staffOrgUnit: staffOrgUnit // Keep staff org unit for backend
        }));
      }
   };

  // NEW: Handle reporting to selection
  const handleReportingToChange = (selectedReportingTo) => {
    console.log('Reporting to selected:', selectedReportingTo);
    setFormData(prev => ({
      ...prev,
      reportingTo: selectedReportingTo
    }));
  };

  // Handle clear staff assignment
  const handleClearStaff = () => {
    setFormData(prev => ({
      ...prev,
      currentIncumbent: '',
      status: 'Vacant'
    }));
  };

  // Handle search input change
  const handleSearchChange = ({ value }) => {
    setSearchTerm(value);
  };

  // Handle status change - clear incumbent if status is Vacant
  const handleStatusChange = (selectedStatus) => {
    if (selectedStatus === 'Vacant') {
      setFormData(prev => ({
        ...prev,
        status: selectedStatus,
        currentIncumbent: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        status: selectedStatus
      }));
    }
  };

  
  // Load staff EXACTLY the same way as StaffList
useEffect(() => {
    let isMounted = true;

    const fetchStaffForDialog = async () => {
        try {
            console.log("🔍 PositionDialog: Loading hierarchical staff list…");
            setStaffOptions([]);

            const { me } = await engine.query({
                me: {
                    resource: "me",
                    params: { fields: "organisationUnits[id,name,level]" }
                }
            });

            if (!me.organisationUnits?.length) {
                console.error("No org units assigned to current user");
                return;
            }

            const primaryOrgUnit = me.organisationUnits[0];
            console.log("📍 User org unit:", primaryOrgUnit);

            const usersResp = await engine.query({
                users: {
                    resource: "users",
                    params: {
                        fields:
                            "id,firstName,surname,email,userCredentials[username],organisationUnits[id,name,level]",
                        pageSize: 2000,
                        filter: "userCredentials.disabled:eq:false"
                    }
                }
            });

            const allUsers = usersResp.users?.users || [];
            console.log("👥 Total DHIS2 users:", allUsers.length);

            const teisResp = await engine.query({
                teis: {
                    resource: "trackedEntityInstances",
                    params: {
                        trackedEntityType: STAFF_TEI_TYPE,
                        fields:
                            "trackedEntityInstance,orgUnit,attributes[attribute,value]",
                        ou: primaryOrgUnit.id,
                        ouMode: "DESCENDANTS",
                        pageSize: 2000
                    }
                }
            });

            const allTeis = teisResp.teis?.trackedEntityInstances || [];
            console.log("📌 TEIs found in hierarchy:", allTeis.length);

            const hierarchyOrgUnitIds = new Set(allTeis.map((tei) => tei.orgUnit));

            const matchedStaff = allUsers
                .map((user) => {
                    const userTeis = allTeis.filter((tei) => {
                        const usernameAttr = tei.attributes?.find(
                            (a) => a.attribute === STAFF_ATTRIBUTES.USERNAME
                        );
                        return (
                            usernameAttr?.value === user.userCredentials?.username
                        );
                    });

                    const userOrgUnits =
                        user.organisationUnits?.map((ou) => ou.id) || [];

                    const isInHierarchy =
                        userTeis.length > 0 ||
                        userOrgUnits.some((ouId) =>
                            hierarchyOrgUnitIds.has(ouId)
                        );

                    return {
                        ...user,
                        hasTei: userTeis.length > 0,
                        tei: userTeis[0] || null,
                        isInHierarchy
                    };
                })
                .filter((staff) => staff.isInHierarchy);

            console.log("✅ Staff inside hierarchy:", matchedStaff.length);

            if (!isMounted) return;

            const staffOptions = matchedStaff.map((staff) => {
                const firstName = staff.firstName || "";
                const surname = staff.surname || "";
                const username = staff.userCredentials?.username || "";
                const email = staff.email || "";

                
                if (staff.organisationUnits?.length > 0) {
                    const deepestOu = [...staff.organisationUnits].sort(
                        (a, b) => b.level - a.level
                    )[0];
                    
                }

                const displayName =
                    firstName || surname
                        ? `${firstName} ${surname}`
                        : username || `User ${staff.id?.slice(0, 8)}`;

                return {
                    id: staff.id,
                    name: displayName,                    
                    email,
                    username,
                    displayText: `${displayName}`,
                    searchText: `${displayName} ${email}`.toLowerCase(),
                    rawStaff: staff
                };
            });

            staffOptions.sort((a, b) => a.name.localeCompare(b.name));

            if (isMounted) {
                setStaffOptions(staffOptions);
            }
        } catch (error) {
            console.error("❌ PositionDialog staff fetch error:", error);
        }
    };

    fetchStaffForDialog();

    return () => {
            isMounted = false;
        };
    }, [engine]);


  useEffect(() => {
      if (!position && formData.positionTitle) { 
        const generatedId = generatePositionId(formData.positionTitle);
        console.log('🆔 Generating Position ID:', {
          title: formData.positionTitle,
          generatedId: generatedId
        });
        setFormData(prev => ({
          ...prev,
          positionId: generatedId
        }));
      } else if (!position && !formData.positionTitle) { 
        setFormData(prev => ({
          ...prev,
          positionId: ''
        }));
      }
   }, [formData.positionTitle, position]);

  
  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      console.log('Dialog closed, resetting form state');
      setFormData({
        positionId: '',
        positionTitle: '',       
        jobGrade: '',
        status: 'Active',
        currentIncumbent: '',
        reportingTo: ''
      });
      setStaffOptions([]);
      setSearchTerm('');
      setIsSubmitting(false);
      setPositionHistory([]);
      setShowHistory(false);
      setReportingOptions([]);
    }
  }, [open]);

  useEffect(() => {
  console.log('🔍 rootOrgUnit state:', rootOrgUnit);
  console.log('🔍 currentUser state:', currentUser?.organisationUnits);
}, [rootOrgUnit, currentUser]);


// Handle save with loading state
const handleSave = async () => {
  if (isSubmitting) {
    console.log('Already submitting, ignoring duplicate click');
    return;
  }

  // ✅ Validate: Position cannot report to itself
  if (position && position.trackedEntityInstance && formData.reportingTo === position.trackedEntityInstance) {
    alert('❌ A position cannot report to itself. Please select a different reporting position or leave it empty.');
    setIsSubmitting(false);
    return;
  }

  if (!position && formData.positionId && formData.reportingTo) {
    // Check if the selected reporting position has the same position ID
    const selectedReportingOption = reportingOptions.find(opt => opt.value === formData.reportingTo);
    if (selectedReportingOption && selectedReportingOption.positionId === formData.positionId) {
      alert('❌ A position cannot report to itself. Please select a different reporting position.');
      setIsSubmitting(false);
      return;
    }
  }

  const finalPositionId = formData.positionId || 
    (formData.positionTitle ? generatePositionId(formData.positionTitle) : '');

  // ✅ FIXED: Better orgUnit determination with fallbacks
  let positionOrgUnit = null;
  
  console.log('🔍 Determining orgUnit:', {
    staffOrgUnit: formData.staffOrgUnit,
    rootOrgUnit: rootOrgUnit?.id,
    positionExists: !!position,
    positionOrgUnit: position?.orgUnit,
    currentUserOrgs: currentUser?.organisationUnits?.map(ou => ({id: ou.id, name: ou.name}))
  });

  // Priority 1: Use staff's org unit if staff is selected
  if (formData.staffOrgUnit) {
    positionOrgUnit = formData.staffOrgUnit;
    console.log('✅ Using staff org unit:', positionOrgUnit);
  }
  // Priority 2: Use root org unit if available
  else if (rootOrgUnit?.id) {
    positionOrgUnit = rootOrgUnit.id;
    console.log('✅ Using root org unit:', positionOrgUnit, rootOrgUnit.name);
  }
  // Priority 3: For editing, try to get orgUnit from existing position
  else if (position?.orgUnit) {
    positionOrgUnit = position.orgUnit;
    console.log('✅ Using existing position org unit:', positionOrgUnit);
  }
  // Priority 4: Use current user's first org unit as last resort
  else if (currentUser?.organisationUnits?.[0]?.id) {
    positionOrgUnit = currentUser.organisationUnits[0].id;
    console.log('✅ Using current user org unit as fallback:', positionOrgUnit);
  }

  // If STILL no orgUnit, show specific error
  if (!positionOrgUnit) {
    console.error('❌ No orgUnit could be determined:', {
      staffOrgUnit: formData.staffOrgUnit,
      rootOrgUnit,
      currentUserOrgs: currentUser?.organisationUnits,
      positionOrgUnit: position?.orgUnit
    });
    
    let errorMessage = 'Organisation unit could not be determined. ';
    
    if (!formData.currentIncumbent || formData.currentIncumbent === 'VACANT') {
      errorMessage += 'For vacant positions, please wait for system to load organisation information. ';
    } else {
      errorMessage += 'The selected staff member may not have an organisation unit assigned. ';
    }
    
    errorMessage += 'Please try again or contact support.';
    
    alert(errorMessage);
    setIsSubmitting(false);
    return;
  }

  const saveData = {
    ...formData,      
    positionId: finalPositionId,
    orgUnit: positionOrgUnit, 
  };

  console.log('💾 Starting save process with data:', saveData);
  setIsSubmitting(true);

  // Basic validation for required fields
  const requiredFields = {
    positionTitle: 'Position Title',
  };
  
  const missingFields = [];
  Object.entries(requiredFields).forEach(([field, label]) => {
    if (!saveData[field] || saveData[field].toString().trim() === '') {
      missingFields.push(label);
    }
  });
  
  if (missingFields.length > 0) {
    alert(`Please fill in the following required fields:\n${missingFields.join('\n')}`);
    setIsSubmitting(false);
    return;
  }

  // Check if staff is already assigned to another active position
  if (formData.currentIncumbent && formData.currentIncumbent !== 'VACANT') {
    const conflictingPosition = positionsList.find(pos => {
      const attrs = extractAttributes(pos.attributes);
      const incumbent = attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
      const status = (attrs[POSITION_ATTRIBUTES.POSITION_STATUS] || 'Active').toLowerCase();
      const isActive = status === 'active';
      const isSame = pos.trackedEntityInstance === position?.trackedEntityInstance;
      return incumbent === formData.currentIncumbent && isActive && !isSame;
    });

    if (conflictingPosition) {
      const attrs = extractAttributes(conflictingPosition.attributes);
      const title = attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Unknown';
      const posId = attrs[POSITION_ATTRIBUTES.POSITION_ID] || conflictingPosition.trackedEntityInstance;

      alert(
        `❌ This staff member is already assigned to another active position:\n\n` +
        `"${title}" (${posId})\n` +
        `Please vacate that position first before assigning them here.`
      );
      setIsSubmitting(false);
      return;
    }
  }

  try {
    console.log('Calling onSave with data:', saveData);
    const onSaveResult = await onSave(saveData);

    // 🔄 Use manageReportingAccess from the utility
    if (formData.currentIncumbent && 
        formData.currentIncumbent !== 'VACANT' && 
        formData.currentIncumbent !== '' &&
        formData.reportingTo && 
        formData.reportingTo.trim() !== '') {
      
      console.log('🔍 Conditions met for reporting access management...');
      
      // Use the imported manageReportingAccess function
      const accessGranted = await manageReportingAccess(
        engine, // Pass the engine instance
        positionsList, // Pass the positions list
        position?.trackedEntityInstance, // Current position TEI (if editing)
        formData.currentIncumbent, // Staff ID
        formData.reportingTo // Reporting TEI
      );
      
      if (accessGranted) {
        console.log('✅ Reporting access managed successfully');
      } else {
        console.warn('⚠️ Reporting access management failed (non-critical error)');
        // Non-critical error - don't fail the whole save
      }
    } else {
      console.log('ℹ️ Reporting access not managed - missing conditions:', {
        hasIncumbent: !!formData.currentIncumbent,
        notVacant: formData.currentIncumbent !== 'VACANT',
        hasReportingTo: !!formData.reportingTo
      });
    }

    // 📋 Keep existing history logic
    if (formData.currentIncumbent && formData.currentIncumbent !== 'VACANT') {
      const positionTeiId = position?.trackedEntityInstance || onSaveResult?.trackedEntityInstance;
      if (positionTeiId) {
        await historyService.trackStaffAssignment(
          positionTeiId,
          formData.currentIncumbent,
          null,
          'Position created/updated with staff assignment',
          currentUser
        );
        console.log('✅ History event created successfully');
      }
    }

    // 🎉 Success handling
    console.log('✅ Position saved successfully');
    onClose(); // This will close the dialog
    
  } catch (error) {
    console.error('❌ Error in handleSave:', error);
    
    // More detailed error messages
    if (error.message?.includes('409') || error.message?.includes('Conflict')) {
      alert('❌ A position with this Position ID already exists. Please use a different Position ID.');
    } else if (error.message?.includes('400') || error.message?.includes('Bad Request')) {
      alert('❌ Invalid data format. Please check all fields and try again.');
    } else {
      alert('❌ An error occurred while saving the position. Please try again.');
    }
    
  } finally {
    setIsSubmitting(false);
  }
};
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getSafeReportingValue = (selectedValue, options) => {
      if (!selectedValue) return '';
      
      const valueExists = options.some(option => option.value === selectedValue);
      
      if (valueExists) {
        return selectedValue;
      } else {
        console.warn(`Selected reporting position "${selectedValue}" not found in reporting options. Resetting to empty.`);
        return '';
      }
  };

  // Handle cancel button click
  const handleCancel = () => {
    console.log('Cancel button clicked - calling onClose');
    if (onClose && typeof onClose === 'function') {
      onClose();
    } else {
      console.error('onClose is not a function or is undefined');
    }
  };

  // Handle modal close (X button or backdrop click)
  const handleModalClose = (event) => {
    console.log('Modal close triggered (X button or backdrop)', event);
    if (onClose && typeof onClose === 'function') {
      onClose();
    } else {
      console.error('onClose is not a function or is undefined');
    }
  };

  const getSafeSelectedValue = (selectedValue, options) => {
      if (!selectedValue) return '';
      
      if (selectedValue === 'VACANT') return 'VACANT';
      
      const valueExists = options.some(option => option.id === selectedValue);
      
      if (valueExists) {
        return selectedValue;
      } else {
        console.warn(`Selected staff value "${selectedValue}" not found in options. Resetting to empty.`);
        return '';
      }
  };

  console.log('PositionDialog render - open:', open, 'isSubmitting:', isSubmitting);

 

  // Helper: get display label for legacy reportingTo
  const getReportingDisplayLabel = (rawValue, options, allPositions) => {
    if (!rawValue) return null;

    // If it's in current valid options, no special message needed
    const isValid = options.some(opt => opt.value === rawValue);
    if (isValid) return null;

    // Try to find it in full positions list for a friendly label
    if (allPositions && allPositions.length > 0) {
      const legacyPos = allPositions.find(pos => pos.trackedEntityInstance === rawValue);
      if (legacyPos) {
        const attrs = extractAttributes(legacyPos.attributes);
        const title = attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Unknown Position';
        const posId = attrs[POSITION_ATTRIBUTES.POSITION_ID] || rawValue;
        return `Previously assigned to: ${title} (${posId})`;
      }
    }

       return `Previously assigned to: [Deleted or inaccessible position]`;
  };

 const renderFormField = (label, children, required = true) => (
        <div style={{ marginBottom: '5px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                {label} {required && <span style={{ color: 'red' }}>*</span>}
            </div>
            {children}
        </div>
    );

  if (isLoading) {
    return (
      <Modal 
        open={open} 
        onClose={handleModalClose}
        large
        dataTest="position-dialog"
      >
        <ModalContent>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '20px' }}>
            <CircularLoader small />
            <span>Loading position data...</span>
          </div>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal 
      open={open} 
      onClose={handleModalClose}
      large
      dataTest="position-dialog"
    >
      <ModalTitle>
        {position ? 'Edit Position' : 'Create New Position'}
      </ModalTitle>
      <ModalContent>
        {/* Form content */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '16px',
          marginBottom: '16px'
        }}>
          {/* Position ID */}
          <div>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ 
                fontWeight: 'bold', 
                color: '#2C6693',
                fontSize: '14px',
                display: 'block'
              }}>
                Position ID 
              </label>
            </div>
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#f8f9fa',
              border: formData.positionId ? '2px solid #198754' : '1px solid #e0e0e0',
              borderRadius: '4px',
              fontSize: '14px',
              color: '#333',
              minHeight: '36px',
              display: 'flex',
              alignItems: 'center',
              fontWeight: formData.positionId ? 'bold' : 'normal'
            }}>
              {formData.positionId ? (
                <span>🎯 {formData.positionId}</span>
              ) : (
                <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                  Will be auto-generated when you fill Title
                </span>
              )}
            </div>
          </div>

          {/* Position Title */}
          <div>
            {renderFormField('Position Title', (
            <InputField
              value={formData.positionTitle}
              onChange={({ value }) => handleInputChange('positionTitle', value)}
              required
              placeholder="e.g., Senior Finance Manager"
              helpText="Enter the full position title"
            />
            ))}
          </div>
            

          {/* Job Grade - Free Text Input */}
          <div>
            {renderFormField('Job Grade', (
            <InputField
              value={formData.jobGrade || ''}
              onChange={({ value }) => handleInputChange('jobGrade', value)}
              placeholder="e.g., Senior Manager, Director, VP, etc."
              helpText="Enter any job grade title"
             />
             ))}
            
            {/* Optional: Show recently used job grades for convenience */}
            {recentJobGrades.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  Recently used:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {recentJobGrades.map((grade, index) => (
                    <button
                      key={index}
                      onClick={() => handleInputChange('jobGrade', grade)}
                      style={{
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #ddd',
                        borderRadius: '12px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        color: '#333'
                      }}
                    >
                      {grade}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>


          
          {/* Reporting To */}
              <div>
                {renderFormField('Reporting To (Optional)', (
                  <SingleSelect
                    selected={formData.reportingTo && reportingOptions.some(opt => opt.value === formData.reportingTo)
                      ? formData.reportingTo
                      : ''
                    }
                    onChange={({ selected }) => {
                      // ✅ Additional client-side validation
                      if (position && position.trackedEntityInstance && selected === position.trackedEntityInstance) {
                        alert('❌ A position cannot report to itself. Please select a different position.');
                        return;
                      }
                      handleReportingToChange(selected);
                    }}
                    placeholder="Select reporting position (can be set later)..."
                    filterable
                    filterPlaceholder="Search positions..."
                    noMatchText="No positions found matching your search"
                    helpText="Optional: Select which position this position reports to"
                  >
                    <SingleSelectOption value="" label="No Reporting Position (set later)" />
                    {reportingOptions.map((option) => (
                      <SingleSelectOption
                        key={option.id}
                        value={option.value}
                        label={option.label}
                      />
                    ))}
                  </SingleSelect>
                ))}

                {/* 👇 UX hint for legacy/inaccessible reporting position */}
                {formData.reportingTo &&
                !reportingOptions.some(opt => opt.value === formData.reportingTo) && (
                  <div style={{
                    marginTop: '8px',
                    padding: '6px 10px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '4px',
                    color: '#856404',
                    fontSize: '13px'
                  }}>
                    <strong>ℹ️ Note:</strong> {getReportingDisplayLabel(formData.reportingTo, reportingOptions, positionsList)}
                  </div>
                )}
              </div>

          {/* Status */}
          <div>
            {renderFormField('Status', (
            <SingleSelect
              selected={formData.status}
              onChange={({ selected }) => handleStatusChange(selected)}
              required
            >
              <SingleSelectOption value="Active" label="Active" />
              <SingleSelectOption value="Vacant" label="Vacant" />
              <SingleSelectOption value="Inactive" label="Inactive" />
            </SingleSelect>
            ))}
          </div>

          {/* Current Incumbent */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ 
                fontWeight: 'bold', 
                fontSize: '14px',
                display: 'block'
              }}>
                Current Incumbent
              </label>
              {formData.currentIncumbent && (
                <Button
                  small
                  destructive
                  onClick={handleClearStaff}
                  style={{ fontSize: '12px', padding: '4px 8px' }}
                >
                  Clear
                </Button>
              )}
            </div>
            
            <SingleSelect
              selected={getSafeSelectedValue(formData.currentIncumbent, staffOptions) || ""}
              onChange={({ selected }) => handleStaffSelection(selected)}
              placeholder={staffOptions.length > 0 ? "Select staff or keep vacant..." : "No staff available"}
              filterable
              filterPlaceholder="Search staff by name..."
              onFilterChange={handleSearchChange}
              noMatchText="No staff members found matching your search"
              maxHeight="400px"
              disabled={formData.status === 'Inactive'}
            >
              <SingleSelectOption value="VACANT" label="🚫 Keep Position Vacant" />
              {staffOptions.map((staff) => (
                <SingleSelectOption
                  key={staff.id}
                  value={staff.id}
                  label={staff.displayText}
                />
              ))}
            </SingleSelect>
          </div>
        </div>

        {/* ... rest of the modal content ... */}

        <div style={{ 
          fontSize: '12px', 
          color: '#6C757D', 
          marginTop: '8px',
          fontStyle: 'italic'
        }}>
          * Required fields
        </div>
      </ModalContent>
      <ModalActions>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
        <button 
          onClick={handleCancel}
          style={{ backgroundColor: "#fcc10dff",
                        color: "#000",
                        border: 'none',
                        boxShadow: '0 2px 4px rgba(122, 118, 118, 1)',
                        borderRadius: '5px',
                        width: '100px',
                        height: '35px',
                        color: '#ffffffff',
                        cursor: 'pointer',
                          }}
          disabled={isSubmitting}
          dataTest="cancel-button"
        >
          Cancel
        </button>
        <button 
          onClick={handleSave}
          style={{ backgroundColor: "#083ea2ff",
                        color: "#000",
                        border: 'none',
                        boxShadow: '0 2px 4px rgba(122, 118, 118, 1)',
                        borderRadius: '5px',
                        width: '140px',
                        height: '35px',
                        color: '#ffffffff',
                        cursor: 'pointer',
                          }}
          disabled={isSubmitting || !formData.positionId} // Removed reportingTo condition
          loading={isSubmitting}
          dataTest="save-button"
        >
          {isSubmitting ? 'Creating...' : (position ? 'Update Position' : 'Create Position')}
        </button>
        </div>
      </ModalActions>
    </Modal>
  );
};

export default PositionDialog;